import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Users, Calendar, Target, TrendingUp, CheckCircle2 } from 'lucide-react';

const COLORS = {
  primary: "#003087",
  slate: "#64748B",
  slateLight: "#94a3b8"
};

const PIE_COLORS = ['#003087', '#1e40af', '#3b82f6', '#60a5fa', '#93c5fd'];

const MiniTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-2 shadow-sm rounded-md text-[10px] font-bold">
        <p className="text-slate-900 dark:text-white">{payload[0].name}: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

export const CounselorDetailView = ({ data, isLoading }) => {
  if (isLoading) return <div className="h-64 flex items-center justify-center text-xs text-slate-400 font-medium">Loading analysis...</div>;
  if (!data) return null;

  const sessionTypesData = data.sessionTypes?.map((item, index) => ({
    name: item._id || 'General',
    value: item.count
  })) || [];

  const weeklyLoadData = data.weeklyLoad?.map(item => ({
    day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][item._id - 1] || 'Day',
    count: item.count
  })) || [];

  const kpis = [
    { label: "Sessions", value: data.completedSessions || 0, icon: CheckCircle2 },
    { label: "Bookings", value: data.totalAppointments || 0, icon: TrendingUp },
    { label: "Retention", value: `${data.retentionRate || 0}`, icon: Target },
    { label: "Clients", value: data.totalUniqueClients || 0, icon: Users }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-top-1 duration-300">
      {/* KPI Row - Minimal but Readable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
            <div className="p-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm">
              <k.icon className="h-4 w-4 text-slate-500" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter leading-none mb-1.5">{k.label}</p>
              <p className="text-base font-bold text-slate-900 dark:text-white leading-none">{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Service Distribution - Expanded */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Service Distribution</h4>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sessionTypesData}
                  innerRadius={45}
                  outerRadius={65}
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="name"
                >
                  {sessionTypesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<MiniTooltip />} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: '10px', fontWeight: '600', paddingTop: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly Intensity - Expanded */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Weekly Intensity</h4>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyLoadData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: '600' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: '600' }} />
                <Tooltip cursor={{ fill: '#f8fafc' }} content={<MiniTooltip />} />
                <Bar dataKey="count" fill={COLORS.primary} radius={[3, 3, 0, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
